import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';

/**
 * Shows how many people are registered.
 *
 * Cognito's ListUsers is an IAM-authenticated admin API, so the browser cannot
 * count users itself. This reads a small Lambda (see lambda/signup-count) that
 * returns { confirmed, accounts, additional } — a total head count plus the
 * split between registrants and the extra people they're bringing. Renders
 * nothing until REACT_APP_SIGNUP_COUNT_URL is set, and nothing if it fails.
 */
type Counts = { confirmed: number; accounts: number; additional: number };

export default function SignupCount(): JSX.Element | null {
  const endpoint = process.env.REACT_APP_SIGNUP_COUNT_URL;
  const { username } = useAuth();
  const [counts, setCounts] = useState<Counts | null>(null);

  useEffect(() => {
    if (!endpoint) return;

    let cancelled = false;
    fetch(endpoint)
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then(body => {
        if (cancelled || typeof body.confirmed !== 'number') return;
        setCounts({
          confirmed: body.confirmed,
          accounts: typeof body.accounts === 'number' ? body.accounts : body.confirmed,
          additional: typeof body.additional === 'number' ? body.additional : 0,
        });
      })
      .catch(() => {
        // Leave the card hidden rather than showing a broken count.
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  if (counts === null) return null;

  const { confirmed, accounts, additional } = counts;

  const body = (
    <>
      <h2>🏕️ Who's Coming</h2>
      <p>
        <strong>{confirmed}</strong> {confirmed === 1 ? 'person is' : 'people are'} coming on the
        trip so far.
      </p>
      {additional > 0 && (
        <p>
          <em>
            That's {accounts} {accounts === 1 ? 'adult' : 'adults'} plus {additional}{' '}
            {additional === 1 ? 'child' : 'children'}.
          </em>
        </p>
      )}
      {username && <p className="card-cta">See who's coming →</p>}
    </>
  );

  // Signed out there's nothing to click through to - the attendee list is
  // behind login, so the card stays a plain, inert card.
  if (!username) return <div className="card">{body}</div>;

  return (
    <Link to="/attendees" className="card card-clickable">
      {body}
    </Link>
  );
}
